import React, { useState, useEffect } from 'react';
import AddWarrantyModal from './AddWarrantyModal';
import WarrantyDetailsModal from './WarrantyDetailsModal';
import { Button, Table, Tag, notification } from 'antd';
import { startCase } from 'lodash';
import moment from 'moment-timezone';
import { AppStore, CurrentUserStore, ShopStore } from 'stores';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Logger } from 'stores/Classes';
import WarrantyStore from 'stores/WarrantyStore';
import { webServiceProvider } from 'shared';
import { EditOutlined, PlusCircleOutlined } from '@ant-design/icons';

const FormattedTable = styled(Table)`
  .ant-table-container {
    overflow-x: auto;
  }

  .ant-table-wrapper {
    width: 100%;
  }

  .ant-table {
    width: 100%;
  }

  .ant-table-pagination.ant-pagination {
    display: flex;
    width: 100%;
    justify-content: center;
    margin-top: 12px;
  }

  .ant-pagination-options {
    display: none;
  }
`;

const WarrantiesTable = ({
  data = [],
  assets = [],
  vendors = [],
  isLoading = false,
  hasSelection = false,
  onUpdate = null,
  onChangePagination = null,
  preSelectedAssets = [],
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isAddWarrantyModalVisible, setIsAddWarrantyModalVisible] =
    useState(false);
  const [warrantyToEdit, setWarrantyToEdit] = useState(null);
  const [isUpdatingWarranty, setIsUpdatingWarranty] = useState(false);
  const [isSavingWarranties, setIsSavingWarranties] = useState(false);
  const [pagination, setPagination] = useState({});
  const [sortParams, setSortParams] = useState({});
  const [filterParams, setFilterParams] = useState({});

  const showEditWarrantyModal = async (record) => {
    try {
      AppStore.addLoading('Getting warranty components');
      const { components } = await WarrantyStore.getWarrantyComponents(
        record.id
      );
      record.components = components;
      setWarrantyToEdit(record);
    } catch (error) {
      Logger.error('Error getting warranty components', error);
      AppStore.addError('Error getting warranty components');
    } finally {
      AppStore.removeLoading('Getting warranty components');
    }
  };

  const handleWarrantyDetailsModalSubmit = async ({ id, ...warrantyData }) => {
    try {
      setIsUpdatingWarranty(true);

      if (warrantyData.documents?.length) {
        const newDocuments = warrantyData.documents.filter(
          (document) => !document.url
        );
        const newDocumentsAfterUpload = await Promise.all(
          newDocuments.map(async (file) => {
            const formData = new FormData();
            formData.append('singleInputFileName', file);
            try {
              const data = await webServiceProvider.postFormData(
                'dashboard/v1/uploadimage',
                formData
              );
              if (data.fileUrl) {
                return {
                  name: file.name,
                  url: data.fileUrl,
                };
              }
            } catch {
              return null;
            }
          })
        );
        const validNewDocuments = newDocumentsAfterUpload.filter((doc) => doc);
        warrantyData.documents = [
          ...warrantyData.documents.filter((doc) => doc.url),
          ...validNewDocuments,
        ];
      }

      await WarrantyStore.updateWarranty(id, {
        ...warrantyData,
        shopId: ShopStore.currentShop.id,
        carId: warrantyData.assets[0],
        name: warrantyData.warrantyName,
        coverageType: warrantyData.coverageType,
        contactId: warrantyData.vendor,
        startDate: warrantyData.startDate,
        endDate: warrantyData.endDate,
        coverageDuration: warrantyData.coverageDuration,
        coverageDurationUnit: warrantyData.coverageDurationPeriod,
        startMeter: warrantyData.startMeter,
        endMeter: warrantyData.endMeter,
        rangeMeter: warrantyData.range,
        documents: warrantyData.documents,
        components: warrantyData.components,
      });

      notification.success({
        message: 'Success',
        description: 'Warranty updated successfully',
      });

      setWarrantyToEdit(null);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      Logger.error(error);
    } finally {
      setIsUpdatingWarranty(false);
    }
  };

  const handleWarrantyModalSubmit = async (warrantiesToAdd) => {
    try {
      setIsSavingWarranties(true);

      const formattedWarranties = warrantiesToAdd.reduce((acc, warranty) => {
        warranty.assets.forEach((assetId) => {
          // if the assetId is not a number, ignore it
          if (isNaN(assetId)) {
            return;
          }
          acc.push({
            carId: assetId,
            name: warranty.warrantyName,
            coverageType: warranty.coverageType,
            contactId: warranty.vendor,
            startDate: warranty.startDate,
            endDate: warranty.endDate,
            coverageDuration: warranty.coverageDuration,
            coverageDurationUnit: warranty.coverageDurationPeriod,
            startMeter: warranty.startMeter,
            endMeter: warranty.endMeter,
            rangeMeter: warranty.range,
            documents: warranty.documents,
            components: warranty.components,
          });
        });
        return acc;
      }, []);
      // upload documents if any
      await Promise.all(
        formattedWarranties.map(async (warranty) => {
          if (warranty.documents?.length) {
            const documents = await Promise.all(
              warranty.documents.map(async (file) => {
                const formData = new FormData();
                formData.append('singleInputFileName', file);
                try {
                  const data = await webServiceProvider.postFormData(
                    'dashboard/v1/uploadimage',
                    formData
                  );
                  if (data.fileUrl) {
                    return {
                      name: file.name,
                      url: data.fileUrl,
                    };
                  }
                } catch {
                  return null;
                }
              })
            );
            warranty.documents = documents.filter((doc) => doc);
          }
        })
      );

      await WarrantyStore.createWarranties(
        formattedWarranties.map((warrantyToAdd) => ({
          ...warrantyToAdd,
          shopId: ShopStore.currentShop.id,
        }))
      );

      notification.success({
        message: 'Success',
        description: 'Warranties added successfully',
      });

      setIsAddWarrantyModalVisible(false);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      Logger.error(error);
    } finally {
      setIsSavingWarranties(false);
    }
  };

  const statusColors = {
    Active: 'green',
    'Expiring Soon': 'orange',
    Expired: 'red',
  };

  const userTimezone =
    CurrentUserStore.user?.settings?.timezone || 'America/Toronto';
  const miKmUserSettings =
    CurrentUserStore.user?.settings?.odometer === 'km' ? 'km' : 'mi';

  const columns = [
    {
      title: 'Unit ID',
      dataIndex: 'id_car',
      key: 'id_car',
      render: (id_car) => {
        const asset = assets.find((asset) => asset.id === id_car);
        return asset ? asset.unitId || asset.carName : id_car;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Expiring Soon', value: 'Expiring Soon' },
        { text: 'Expired', value: 'Expired' },
      ],
      sorter: true,
      render: (status) => (
        <Tag color={statusColors[status]} key={status}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Coverage Type',
      dataIndex: 'coverage_type',
      key: 'coverage_type',
      sorter: true,
      render: (coverage_type) => startCase(coverage_type),
    },
    {
      title: 'Vendor',
      dataIndex: 'id_contact',
      key: 'id_contact',
      render: (vendorId) => {
        const vendor = vendors.find((vendor) => vendor.id === vendorId);
        return vendor ? vendor.name : vendorId;
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (start_date) => moment(start_date).tz(userTimezone).format('ll'),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      sorter: true,
      render: (end_date) => moment(end_date).tz(userTimezone).format('ll'),
    },
    {
      title: 'Start Meter',
      dataIndex: 'start_meter',
      key: 'start_meter',
      render: (start_meter) => `${start_meter} ${miKmUserSettings}`,
    },
    {
      title: 'Range',
      dataIndex: 'range_meter',
      key: 'range_meter',
      render: (range_meter) => `${range_meter} ${miKmUserSettings}`,
    },
    {
      title: 'End Meter',
      dataIndex: 'end_meter',
      key: 'end_meter',
      sorter: true,
      render: (end_meter) => `${end_meter} ${miKmUserSettings}`,
    },
    {
      title: '',
      key: 'action',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => showEditWarrantyModal(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const rowSelection = hasSelection
    ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) =>
          setSelectedRowKeys(newSelectedRowKeys),
      }
    : undefined;

  useEffect(() => {
    if (onChangePagination && Object.keys(sortParams).length > 0) {
      onChangePagination({
        pagination,
        filters: filterParams,
        sorter: sortParams,
      });
    }
  }, [pagination, sortParams, filterParams]);

  const handleTableChange = (paginationParams, filtersParams, sorterParams) => {
    let fullSorter = sorterParams;

    if (Array.isArray(sorterParams)) {
      fullSorter = sorterParams[0] || {};
    }

    setPagination(paginationParams);
    setFilterParams(filtersParams);
    setSortParams(fullSorter);

    if (onChangePagination) {
      onChangePagination({
        pagination: paginationParams,
        filters: filtersParams,
        sorter: fullSorter,
      });
    }
  };

  return (
    <>
      <FormattedTable
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={pagination}
        scroll={{ x: 'max-content' }}
      />
      <Button
        type="link"
        icon={<PlusCircleOutlined />}
        style={{ marginTop: 10 }}
        onClick={() => setIsAddWarrantyModalVisible(true)}
      >
        Add New Warranty
      </Button>
      <AddWarrantyModal
        visible={isAddWarrantyModalVisible}
        finishing={isSavingWarranties}
        preSelectedAssets={preSelectedAssets}
        onOk={handleWarrantyModalSubmit}
        onCancel={() => setIsAddWarrantyModalVisible(false)}
      />
      <WarrantyDetailsModal
        visible={warrantyToEdit !== null}
        saving={isUpdatingWarranty}
        warranty={warrantyToEdit}
        assets={assets}
        vendors={vendors}
        onCancel={() => setWarrantyToEdit(null)}
        onSubmit={handleWarrantyDetailsModalSubmit}
      />
    </>
  );
};

WarrantiesTable.propTypes = {
  hasSelection: PropTypes.bool,
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  assets: PropTypes.array,
  vendors: PropTypes.array,
  onUpdate: PropTypes.func,
  onChangePagination: PropTypes.func,
  preSelectedAssets: PropTypes.array,
};

export default WarrantiesTable;
